import { errorMessage } from "@atlas/shared";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAdminFeatureFlags, updateFeatureFlag, type AdminFeatureFlag } from "../../api/flags";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { FEATURE_FLAGS_KEY } from "../../hooks/useFeatureFlags";
import { AdminShell, AccessDenied } from "./AdminTabs";

const ADMIN_FLAGS_KEY = ["admin/flags"];

function formatWhen(iso: string | null): string {
  if (!iso) return "never changed";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function FlagRow({ flag }: { flag: AdminFeatureFlag }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => updateFeatureFlag(flag.key, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_FLAGS_KEY });
      void queryClient.invalidateQueries({ queryKey: FEATURE_FLAGS_KEY });
    },
  });

  return (
    <div
      className="r-wrap"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 16,
        padding: "14px 0",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
            {flag.label}
          </span>
          <code
            style={{
              fontSize: 11,
              color: "var(--text-muted)",
              background: "var(--surface-sunken)",
              padding: "1px 6px",
              borderRadius: "var(--radius-sm)",
            }}
          >
            {flag.key}
          </code>
        </div>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, margin: "6px 0 4px" }}>
          {flag.description}
        </p>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
          {flag.updatedAt
            ? `Changed ${formatWhen(flag.updatedAt)}${flag.updatedBy ? ` by ${flag.updatedBy}` : ""}`
            : `Using default (${flag.defaultEnabled ? "on" : "off"})`}
          {mutation.isError && (
            <span style={{ color: "var(--status-danger)", marginLeft: 8 }}>
              {errorMessage(mutation.error, "Update failed")}
            </span>
          )}
        </p>
      </div>

      <button
        role="switch"
        aria-checked={flag.enabled}
        onClick={() => mutation.mutate(!flag.enabled)}
        disabled={mutation.isPending}
        style={{
          flexShrink: 0,
          background: "none",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-pill)",
          cursor: mutation.isPending ? "wait" : "pointer",
          padding: "5px 12px",
          fontSize: 12,
          fontWeight: 600,
          minWidth: 64,
          color: flag.enabled ? "var(--status-success)" : "var(--text-muted)",
        }}
      >
        {mutation.isPending ? "Saving…" : flag.enabled ? "On" : "Off"}
      </button>
    </div>
  );
}

export default function AdminFlagsPage() {
  const { user } = useAuth();
  const flagsQuery = useQuery({
    queryKey: ADMIN_FLAGS_KEY,
    queryFn: fetchAdminFeatureFlags,
    enabled: !!user?.isAdmin,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  return (
    <AdminShell title="Flags">
      <AtlasPanel>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, margin: "0 0 10px" }}>
          Runtime switches read by the web app and the mobile apps on every launch. Changes
          take effect within about a minute, with no deploy or app update. Mobile builds
          that predate a flag fall back to the flag's default.
        </p>
        {flagsQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Skel height="56px" />
            <Skel height="56px" />
          </div>
        ) : flagsQuery.isError ? (
          <p style={{ fontSize: 13, color: "var(--status-danger)" }}>Couldn't load flags.</p>
        ) : (
          (flagsQuery.data?.flags ?? []).map((flag) => <FlagRow key={flag.key} flag={flag} />)
        )}
      </AtlasPanel>
    </AdminShell>
  );
}
